import type { CSSProperties } from "react";
import { useAttachmentMetadata } from "@circle-react-shared/BuilderUI/Media/AttachmentContext";
import type { StyleValue } from "@circle-react-shared/BuilderUI/StyleValue.types";
import type { DefaultBlockStyles } from "../DefaultBlocks/Homepage/constants";
import { parseStyles } from "../utils/parseStyles";

export interface StylesBackgroundImageValue {
  placeholder: string;
  signed_id: string;
}

interface UseTopBlockStylesProps {
  defaultStyles: DefaultBlockStyles;
  ignoreDefaults?: boolean;
  styles?: Record<string, StyleValue>;
}

interface UseTopBlockStylesReturn {
  backgroundStyles: CSSProperties;
  borderColorStyles: CSSProperties;
  marginStyles: CSSProperties;
  paddingStyles: CSSProperties;
}

export const useTopBlockStyles = ({
  styles = {},
  defaultStyles,
  ignoreDefaults = true,
}: UseTopBlockStylesProps): UseTopBlockStylesReturn => {
  const { backgroundImage } = styles;
  const { value } = backgroundImage || {};

  const { signed_id: backgroundImageSignedId, placeholder } =
    (value as StylesBackgroundImageValue) || {
      signed_id: "",
      placeholder: "",
    };

  const attachmentMetadata = useAttachmentMetadata(backgroundImageSignedId);
  const { url: attachmentUrl } = attachmentMetadata ?? {};
  const url = attachmentUrl || placeholder;

  const backgroundStyles = parseStyles({
    styles: {
      backgroundImage: {
        type: "background-image",
        value: url,
      },
      backgroundColor: styles.backgroundColor,
    },
    defaultStyles: {
      backgroundImage: defaultStyles.backgroundImage,
      backgroundColor: defaultStyles.backgroundColor,
    },
    ignoreDefaults,
  });

  const paddingStyles = parseStyles({
    styles: { padding: styles.padding },
    defaultStyles: { padding: defaultStyles.padding },
    ignoreDefaults,
  });

  const marginStyles = parseStyles({
    styles: { margin: styles.margin },
    defaultStyles: { margin: defaultStyles.margin },
    ignoreDefaults,
  });

  const borderColorStyles = parseStyles({
    styles: { borderColor: styles.borderColor },
    defaultStyles: { borderColor: defaultStyles.borderColor },
    ignoreDefaults,
  });

  return {
    backgroundStyles,
    borderColorStyles,
    marginStyles,
    paddingStyles,
  };
};
